import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, FileStack, Upload } from 'lucide-react';
import { api } from '../../services/api';
import Badge from '../ui/Badge';
import Field, { FormServerError } from '../ui/Field';
import { formatDateTime } from '../../utils/format';
import { ReviewStateBadge } from './ReviewDetail';

/**
 * FIX-D337 stage ה (D-337 §6-§9, D-346 §2-§4) - "פגישות אפיון" with check boxes, "+ פגישת אפיון",
 * "סיימנו את האפיון: להכין מסמכים" (the kind of engagement, the team lead, the development hours), and "חבילת המסמכים"
 * with each document's state and "העלאת קובץ מוכן" for the annexes. On the characterization card, and on the lead
 * page of a lead that skipped the characterization quote (D-346 §2).
 */
export default function CharacterizationPackagePanel({
  leadId, characterizationId, orgId, canWrite, onAddMeeting,
}: {
  leadId?: string | null;
  characterizationId?: string | null;
  orgId?: string | null;
  canWrite: boolean;
  onAddMeeting?: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ['char-package', leadId ?? null, characterizationId ?? null];
  const ctx = useQuery({
    queryKey: key,
    queryFn: async () => (await api.getPackageContext({ lead_id: leadId ?? null, characterization_id: characterizationId ?? null })).data,
  });
  const agents = useQuery({
    queryKey: ['assignable-agents', orgId ?? ''],
    queryFn: async () => (await api.getAssignableAgents(orgId ?? undefined)).data ?? [],
    enabled: canWrite,
    staleTime: 300_000,
  });
  const [picked, setPicked] = useState<string[] | null>(null);
  const [kind, setKind] = useState<'project' | 'development' | 'service_contract'>('project');
  const [teamLead, setTeamLead] = useState('');
  const [hours, setHours] = useState('100');
  const [idemKey] = useState(() => `char-pkg-${leadId ?? characterizationId}-${Date.now()}`);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadFor, setUploadFor] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const create = useMutation({
    mutationFn: () => api.createPackage({
      lead_id: characterizationId ? null : leadId ?? null,
      characterization_id: characterizationId ?? null,
      meeting_ids: picked ?? [],
      engagement_kind: kind,
      team_lead_user_id: teamLead || null,
      development_hours: Number(hours) || null,
    }, idemKey),
    onSuccess: refresh,
  });
  const changeLead = useMutation({
    mutationFn: ({ id, user }: { id: string; user: string | null }) => api.setPackageTeamLead(id, user),
    onSuccess: refresh,
  });
  const upload = useMutation({
    mutationFn: ({ docId, file }: { docId: string; file: File }) => api.uploadPackageAnnex(docId, file),
    onSuccess: refresh,
  });

  const data = ctx.data;
  if (!data) return null;
  const pkg = data.package;
  const meetings: any[] = data.meetings ?? [];
  const chosen = picked ?? meetings.filter((m) => /characterization/.test(m.meeting_type || '')).map((m) => m.id);

  return (
    <section className="space-y-3 rounded-xl border border-surface-200 bg-surface-0 p-4" aria-labelledby="char-pkg-title">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="char-pkg-title" className="text-sm font-semibold text-surface-900">{t('charPackage.meetingsTitle')}</h3>
        {canWrite && onAddMeeting && !pkg && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={onAddMeeting}>
            <CalendarPlus size={16} aria-hidden="true" /> {t('charPackage.addMeeting')}
          </button>
        )}
      </div>
      {meetings.length === 0 ? <p className="text-sm text-surface-600">{t('charPackage.noMeetings')}</p> : (
        <ul className="space-y-1.5">
          {meetings.map((m) => (
            <li key={m.id}>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!!pkg || !canWrite}
                  checked={pkg ? (pkg.meeting_ids ?? []).includes(m.id) : chosen.includes(m.id)}
                  onChange={(e) => setPicked(e.target.checked ? [...chosen, m.id] : chosen.filter((x) => x !== m.id))}
                />
                <span className="min-w-0 flex-1 truncate">{m.title} · {formatDateTime(m.meeting_datetime)}</span>
                {m.has_transcript ? <Badge tone="success">{t('charQuote.transcribed')}</Badge> : m.has_recording ? <Badge tone="info">{t('charQuote.recorded')}</Badge> : null}
              </label>
            </li>
          ))}
        </ul>
      )}

      {!pkg && canWrite && (
        <div className="space-y-3 border-t border-surface-200 pt-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t('charPackage.kind')} id="pkg-kind">
              <select id="pkg-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                <option value="project">{t('charPackage.kinds.project')}</option>
                <option value="development">{t('charPackage.kinds.development')}</option>
                <option value="service_contract">{t('charPackage.kinds.service_contract')}</option>
              </select>
            </Field>
            <Field label={t('charPackage.teamLead')} id="pkg-tl" hint={t('charPackage.teamLeadHint')}>
              <select id="pkg-tl" className="input" value={teamLead} onChange={(e) => setTeamLead(e.target.value)}>
                <option value="">{t('charPackage.noTeamLead')}</option>
                {(agents.data ?? []).map((a: any) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Field>
            <Field label={t('charPackage.hours')} id="pkg-hours">
              <input id="pkg-hours" className="input" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} />
            </Field>
          </div>
          <button type="button" className="btn btn-primary" disabled={create.isPending} onClick={() => create.mutate()}>
            <FileStack size={16} aria-hidden="true" /> {t('charPackage.finish')}
          </button>
          <p className="text-xs text-surface-600">{t('charPackage.finishHint')}</p>
          <FormServerError error={create.error} fallback={t('docReview.actionError')} />
        </div>
      )}

      {pkg && (
        <div className="space-y-2 border-t border-surface-200 pt-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-surface-900">{t('charPackage.packageTitle')}</h4>
            <Badge tone={pkg.status === 'signed' ? 'success' : pkg.status === 'sent' ? 'info' : 'warning'} dot>{t(`charPackage.status.${pkg.status}`)}</Badge>
          </div>
          <p className="text-xs text-surface-600">
            {t(`charPackage.kinds.${pkg.engagement_kind}`)} · {t('charPackage.teamLead')}: {pkg.team_lead_name || t('charPackage.noTeamLead')}
          </p>
          {/* FIX-D337-2 §3: signed while the lead's conversion waits for a review - opens by itself when it completes. */}
          {pkg.awaiting_conversion && (
            <p role="status" className="rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-800">{t('charPackage.awaitingConversion')}</p>
          )}
          {canWrite && ['with_ote', 'sent'].includes(pkg.status) && (
            <label className="flex flex-wrap items-center gap-2 text-xs">
              <span>{t('charPackage.changeTeamLead')}</span>
              <select className="input h-8 w-48 py-0 text-xs" value={pkg.team_lead_user_id ?? ''}
                onChange={(e) => changeLead.mutate({ id: pkg.id, user: e.target.value || null })}>
                <option value="">{t('charPackage.noTeamLead')}</option>
                {(agents.data ?? []).map((a: any) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
          )}
          <ul className="divide-y divide-surface-200">
            {(pkg.documents ?? []).map((d: any) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <span className="min-w-0">
                  <Link className="link" to={`/documents?doc=${d.id}`}>{d.title}</Link>
                  <span className="ms-2 text-xs text-surface-600">{d.customer_facing ? t('charPackage.toCustomer') : t('charPackage.internal')}</span>
                </span>
                <span className="flex items-center gap-2">
                  {d.customer_facing ? <ReviewStateBadge state={d.review_state} /> : <Badge tone="neutral">{t('charPackage.atDelivery')}</Badge>}
                  {canWrite && ['annex_spec', 'annex_screens'].includes(d.role) && ['draft', 'with_ote', 'returned'].includes(d.review_state) && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setUploadFor(d.id); fileRef.current?.click(); }}>
                      <Upload size={14} aria-hidden="true" /> {t('charPackage.uploadReady')}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <input ref={fileRef} type="file" accept="application/pdf" className="sr-only" tabIndex={-1} aria-hidden="true"
            onChange={(e) => { const f = e.target.files?.[0]; if (f && uploadFor) upload.mutate({ docId: uploadFor, file: f }); e.target.value = ''; }} />
          <FormServerError error={upload.error || changeLead.error} fallback={t('docReview.actionError')} />
        </div>
      )}
    </section>
  );
}
